import { RecentlyViewed } from '@theme/recently-viewed-products';
import { Component } from '@theme/component';
import { sectionRenderer } from '@theme/section-renderer';

class ProductRecentlyViewed extends Component {
  #loaded = false;

  #intersectionObserver = new IntersectionObserver(
    (entries, observer) => {
      if (!entries[0]?.isIntersecting) return;
      observer.disconnect();
      this.#loadRecentlyViewedProducts();
    },
    { rootMargin: '0px 0px 400px 0px' }
  );

  // Watches the parent wrapper for when a tab panel is revealed (hidden removed).
  #parentObserver = new MutationObserver(() => {
    if (!this.parentElement?.hasAttribute('hidden')) {
      this.#parentObserver.disconnect();
      this.#loadRecentlyViewedProducts();
    }
  });

  // Watches this element itself for when hidden is removed directly (e.g. when
  // tab-content sets hidden on the element rather than on a wrapper).
  #selfObserver = new MutationObserver(() => {
    if (!this.hasAttribute('hidden')) {
      this.#selfObserver.disconnect();
      this.#loadRecentlyViewedProducts();
    }
  });

  connectedCallback() {
    super.connectedCallback();
    this.#intersectionObserver.observe(this);

    // Always watch the parent for hidden removal. tab-content.js is also a
    // deferred module, so it may not have set hidden yet when this runs.
    if (this.parentElement) {
      this.#parentObserver.observe(this.parentElement, {
        attributes: true,
        attributeFilter: ['hidden'],
      });
    }

    // Also watch this element directly in case hidden is toggled on it instead
    // of on a wrapper (depends on how Shopify wraps blocks).
    this.#selfObserver.observe(this, {
      attributes: true,
      attributeFilter: ['hidden'],
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#intersectionObserver.disconnect();
    this.#parentObserver.disconnect();
    this.#selfObserver.disconnect();
  }

  #loadRecentlyViewedProducts = async () => {
    if (this.#loaded) return;
    const viewedProducts = RecentlyViewed.getProducts();
    if (viewedProducts.length === 0) return;
    this.#loaded = true;
    this.#intersectionObserver.disconnect();
    this.#parentObserver.disconnect();
    this.#selfObserver.disconnect();

    try {
      const markup = await this.#getRecentlyViewedProductsMarkup();
      if (!markup) return;

      // Extract only this element from the section response to avoid overwriting
      // sibling blocks (e.g. product-recommendations) that maintain their own state.
      const doc = new DOMParser().parseFromString(markup, 'text/html');
      const updated = doc.getElementById(this.id)
        ?? doc.querySelector(`recently-viewed[data-section-id="${CSS.escape(this.dataset.sectionId ?? '')}"]`)
        ?? doc.querySelector('recently-viewed');
      if (!updated) return;

      this.innerHTML = updated.innerHTML;
    } catch (error) {
      this.#loaded = false;
      this.#handleError(error);
    }
  }

  async #getRecentlyViewedProductsMarkup() {
    if (!this.dataset.sectionId) return null;

    const viewedProducts = RecentlyViewed.getProducts();
    if (viewedProducts.length === 0) return null;

    const url = new URL(Theme.routes.search_url, location.origin);
    url.searchParams.set('q', viewedProducts.map((id) => `id:${id}`).join(' OR '));
    url.searchParams.set('type', 'product');

    return sectionRenderer.getSectionHTML(this.dataset.sectionId, false, url);
  }

  /** @param {Error} error */
  #handleError(error) {
    console.error('Recently viewed error:', error.message);
    this.classList.add('hidden');
    this.dataset.error = 'Error loading recently viewed product';
  }
}

if (!customElements.get('recently-viewed')) {
  customElements.define('recently-viewed', ProductRecentlyViewed);
}
